import React, {useEffect, useRef, useState} from 'react';
import {staffApi} from '@/lib/staffApi';

const button = 'rounded-full border border-gold px-5 py-3 text-sm font-medium disabled:opacity-40';

export function RemainingWhatsApp() {
    const [data,setData]=useState(null);
    const [preview,setPreview]=useState(null);
    const [busy,setBusy]=useState(false);
    const [running,setRunning]=useState(false);
    const [error,setError]=useState('');
    const mounted=useRef(true), stop=useRef(true), inFlight=useRef(false);
    const load=async()=>{
        const result=await staffApi.serviceUpdate('whatsapp-remaining');
        if(mounted.current) setData(result);
    };
    useEffect(()=>{
        mounted.current=true;
        load().catch(()=>setError('Could not check remaining WhatsApp recipients. Refresh to try again.'));
        return ()=>{mounted.current=false;stop.current=true;};
    },[]);
    const action=async(fn)=>{
        if(inFlight.current) return;
        inFlight.current=true;setBusy(true);setError('');
        try {await fn();}
        catch(e){if(mounted.current)setError(e.message||'The result could not be confirmed. Refresh before continuing.');}
        finally{inFlight.current=false;if(mounted.current){setBusy(false);setRunning(false);}}
    };
    const batches=async(token)=>{
        stop.current=false;setRunning(true);
        while(!stop.current && mounted.current){
            const run=await staffApi.serviceUpdate('whatsapp-batch',{token});
            if(!mounted.current)return;
            setData(old=>({...old,run}));
            if(run.state!=='running')break;
        }
        if(mounted.current)await load();
    };
    const run=data?.run, counts=data?.counts||{}, capacity=data?.capacity||{};
    const active=run?.state==='running';
    return <section className="rounded-2xl border border-gold/40 p-4 space-y-4" aria-label="Remaining WhatsApp messages">
        <h3 className="text-xl text-white">Remaining WhatsApp messages</h3>
        <p className="text-sm text-white/80">Sent, delivered and read messages are excluded. Unconfirmed results are also excluded to avoid sending twice. Only confirmed unsent recipients and eligible failed attempts can be included.</p>
        {error&&<p role="alert" className="rounded-xl bg-red-950 p-3 text-white">{error}</p>}
        {!data?<p>Checking remaining recipients…</p>:<>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <p><strong className="block text-2xl text-gold">{data.remaining||0}</strong>remaining to retry</p>
                <p><strong className="block text-2xl">{counts.already_sent||0}</strong>already sent — excluded</p>
                <p>{counts.unconfirmed||0} unconfirmed — excluded</p>
                <p>{(counts.needs_review||0)+(counts.contact_changed||0)} need review or have changed contact preferences</p>
            </div>
            {!!counts.cooling_down&&<p className="text-sm text-gold">{counts.cooling_down} failed attempts are waiting for their retry interval. They will only be included after you refresh and choose to send again.</p>}
            <div className="rounded-xl bg-white/5 p-3 space-y-2 text-sm">
                <p>Meta allowance: <strong>{capacity.verified?capacity.limit:'Not verified'}</strong> recipients per rolling 24 hours.</p>
                <p>Keep <strong>{capacity.reserve||100}</strong> places aside for appointment messages. Available for this announcement now: <strong>{capacity.available||0}</strong>.</p>
                {capacity.reason&&<p className="text-gold">{capacity.reason}</p>}
                <p className="text-white/65">Both options respect this allowance. Meta may apply further restrictions; activity outside this app can also use the shared allowance.</p>
            </div>
            {run&&<div role="status" className="text-sm space-y-1">
                <p>This run: {run.processed||0} of {run.total||0} checked · {run.accepted||0} accepted by Meta · {run.skipped||0} skipped or needing review.</p>
                {run.reason&&<p>{run.reason}</p>}
            </div>}
            {!active&&!preview&&<div className="flex flex-wrap gap-3">
                <button className={button+' bg-gold text-[#101a35]'} disabled={busy||!data.next_200} onClick={()=>action(async()=>setPreview(await staffApi.serviceUpdate('whatsapp-preview',{mode:'200'})))}>Send next 200</button>
                <button className={button} disabled={busy||!data.all_remaining} onClick={()=>action(async()=>setPreview(await staffApi.serviceUpdate('whatsapp-preview',{mode:'all'})))}>Send all remaining</button>
                <p className="w-full text-sm text-white/75">Next 200: up to {data.next_200||0} now · All remaining: up to {data.all_remaining||0} now.</p>
            </div>}
            {preview&&<div className="rounded-xl border border-gold p-3 space-y-3" role="group" aria-label="Confirm remaining WhatsApp send">
                <p>Send this update to <strong>{preview.count}</strong> remaining WhatsApp recipients now? Previously sent and unconfirmed messages stay excluded.</p>
                <div className="flex flex-wrap gap-3">
                    <button className={button+' bg-gold text-[#101a35]'} disabled={busy} onClick={()=>action(async()=>{
                        const result=await staffApi.serviceUpdate('whatsapp-start',{token:preview.token,confirmation:'SEND'});
                        setPreview(null);setData(old=>({...old,run:result}));
                        if(result.state==='running')await batches(result.id);else await load();
                    })}>Confirm send {preview.count}</button>
                    <button className={button} disabled={busy} onClick={()=>setPreview(null)}>Cancel</button>
                </div>
            </div>}
            {active&&!running&&<button className={button} disabled={busy} onClick={()=>action(()=>batches(run.id))}>Continue this WhatsApp run</button>}
            {running&&<button className={button} onClick={()=>{stop.current=true;}}>Pause after this batch</button>}
            <button className={button} disabled={busy} onClick={()=>action(async()=>{setPreview(null);await load();})}>Refresh remaining totals and allowance</button>
            <p className="text-sm text-white/65">Nothing sends automatically when the allowance increases. Keep this page open during a send run; you can pause or return later to continue.</p>
        </>}
    </section>;
}
